import React, { useContext, useState, useMemo } from "react";
import { Form } from "antd";
import { ModalForm } from "src/enitities/Modal";
import { PriceForm, serializeTimestamp } from "src/enitities/Form";
import { PricesContext } from "src/shared";
import { API } from "src/shared";
import { apiRequests } from "src/shared/api/api";

export default function AddCategory({ isOpen, setOpen }) {
  const [changeData, setChangeData] = useState(false);
  const { token, pathname, priceTypesData } = useContext(PricesContext);
  const [form] = Form.useForm();

  // Создаем мапу для конвертации имени типа цены в ID
  const priceTypeNameToId = useMemo(() => {
    const map = new Map();
    if (priceTypesData && Array.isArray(priceTypesData)) {
      priceTypesData.forEach((type) => {
        if (type.name && type.id) {
          map.set(type.name, type.id);
        }
      });
    }
    return map;
  }, [priceTypesData]);

  const handleSubmit = async (data) => {
    const newData = serializeTimestamp(data[0], ["date_from", "date_to"]);
    
    // Нормализуем price_type: если строка, конвертируем в ID
    let normalizedPriceType = newData.price_type;
    if (typeof newData.price_type === "string" && priceTypeNameToId.has(newData.price_type)) {
      normalizedPriceType = priceTypeNameToId.get(newData.price_type);
    }
    
    // Проверка на дубли: если указан адрес, проверяем существование цены с таким же адресом, номенклатурой и типом
    if (newData.address && newData.nomenclature && normalizedPriceType) {
      try {
        // Получаем все цены (достаточно много для проверки)
        const existingPrices = await apiRequests.prices.getItems({
          token,
          limit: 10000, // Получаем больше записей для надежной проверки
        });
        
        if (existingPrices.data?.result && Array.isArray(existingPrices.data.result)) {
          // Проверяем, есть ли цены с таким же адресом (точное совпадение без учета регистра)
          const duplicate = existingPrices.data.result.find((price) => {
            // Нормализуем price_type из ответа API
            let existingPriceType = price.price_type;
            if (typeof existingPriceType === "string" && priceTypeNameToId.has(existingPriceType)) {
              existingPriceType = priceTypeNameToId.get(existingPriceType);
            }
            
            // Сравниваем nomenclature (может быть nomenclature_id в ответе)
            const priceNomenclature = price.nomenclature || price.nomenclature_id;
            
            // Сравниваем адреса (без учета регистра и пробелов)
            const normalizedExistingAddress = price.address?.trim().toLowerCase() || "";
            const normalizedNewAddress = newData.address.trim().toLowerCase();
            
            return (
              normalizedExistingAddress === normalizedNewAddress &&
              priceNomenclature === newData.nomenclature &&
              existingPriceType === normalizedPriceType
            );
          });
          
          if (duplicate) {
            const errorMessage = `Цена с таким же адресом "${newData.address}" уже существует для данной номенклатуры и типа цены.`;
            // Выбрасываем ошибку, чтобы остановить выполнение
            // Сообщение будет показано в ModalForm.jsx
            const validationError = new Error(errorMessage);
            validationError.name = "ValidationError";
            throw validationError;
          }
        }
      } catch (err) {
        // Если это ошибка валидации дубля, пробрасываем её дальше
        if (err.name === "ValidationError" || (err.message && err.message.includes("уже существует"))) {
          throw err;
        }
        console.error("Ошибка проверки дублей цен:", err);
        // Продолжаем создание только если это не ошибка валидации
      }
    }
    
    const queryCreate = API.crud.create(token, pathname);
    try {
      await queryCreate([newData]);
      return true;
    } catch (err) {
      // Извлекаем сообщение об ошибке из ответа API
      // API.crud.create пробрасывает исходную ошибку axios
      // Нужно извлечь detail из response.data
      let errorMessage = "Ошибка при создании цены";
      
      if (err?.response?.data) {
        const errorData = err.response.data;
        
        // Приоритет: detail (строка) > detail (массив) > message
        if (errorData.detail) {
          if (typeof errorData.detail === "string") {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail
              .map(e => typeof e === "string" ? e : e?.msg || JSON.stringify(e))
              .join("; ");
          } else {
            errorMessage = JSON.stringify(errorData.detail);
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      // Создаем ошибку с правильным сообщением и сохраняем response
      const apiError = new Error(errorMessage);
      apiError.response = err?.response;
      apiError.name = "APIError";
      throw apiError;
    }
  };

  return (
    <ModalForm
      title={"Добавить цену"}
      width={500}
      isOpen={isOpen}
      setOpen={setOpen}
      formContext={form}
      handleSubmit={handleSubmit}
      changeData={changeData}
      setChangeData={setChangeData}>
      <PriceForm formContext={form} disableNomeclature={false} />
    </ModalForm>
  );
}
